import { Muxer, ArrayBufferTarget } from "mp4-muxer";
import { MediaRecorderResponse } from "./mediarecorder.types";

interface CaptureFrame {
  frame: VideoFrame;
  needsKeyFrame: boolean;
}

export class CanvasRecorder {
  private canvas: HTMLCanvasElement;
  private videoEncoder: VideoEncoder | null = null;
  private audioEncoder: AudioEncoder | null = null;
  private muxer: Muxer<ArrayBufferTarget> | null = null;
  private audioTrack: MediaStreamTrack | null = null;
  private recording = false;
  private startTime: number | null = null;
  private lastKeyFrame = -Infinity;
  private activeFrame: number = 0;
  private frameRate: number = 30;
  private audioProcessor: MediaStreamTrackProcessor;
  private lastFrame: CaptureFrame | null = null;

  private width: number = 0;
  private height: number = 0;

  constructor(canvas: HTMLCanvasElement, width: number, height: number) {
    this.canvas = canvas;
    this.width = width;
    this.height = height;
  }

  public async startRecording(frameRate: number, bitrate = 1e6) {
    this.frameRate = frameRate;

    if (typeof VideoEncoder === 'undefined') {
      throw new Error("VideoEncoder / WebCodecs API not supported.");
    }

    if (typeof AudioEncoder !== 'undefined') {
      try {
        const userMedia = await navigator.mediaDevices.getUserMedia({
          video: false, audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 48000 * 2,

          }
        });
        this.audioTrack = userMedia.getAudioTracks()[0];
      } catch (e) {
        console.warn("Couldn't acquire an audio track.");
      }
    }

    const audioSampleRate = this.audioTrack?.getSettings().sampleRate;
    const audioNumberOfChannels = this.audioTrack?.getSettings().channelCount;


    this.muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: {
        codec: 'avc',
        width: this.width,
        height: this.height,
        // frameRate: frameRate,
      },
      audio: {
        codec: 'aac',
        sampleRate: audioSampleRate!,
        numberOfChannels: audioNumberOfChannels!
      },
      fastStart: 'in-memory',
      firstTimestampBehavior: 'offset',
    });

    this.videoEncoder = new VideoEncoder({
      output: (chunk, meta) => this.muxer!.addVideoChunk(chunk, meta),
      error: console.error,
    });
    this.videoEncoder.configure({
      codec: 'avc1.42001f',
      width: this.width,
      height: this.height,
      bitrate: bitrate,
    });

    if (this.audioTrack) {
      this.audioEncoder = new AudioEncoder({
        output: (chunk, meta) => this.muxer!.addAudioChunk(chunk, meta),
        error: e => console.error(e)
      });

      if (audioSampleRate && audioNumberOfChannels) {
        this.audioEncoder.configure({
          //codec: 'opus',
          codec: 'mp4a.40.2',
          numberOfChannels: audioNumberOfChannels,
          sampleRate: audioSampleRate,
          // bitrate: 34000
        });
      } else {
        console.error("Audio sample rate or number of channels is undefined.");
      }

      // Create a MediaStreamTrackProcessor to get AudioData chunks from the audio track
      this.audioProcessor = new MediaStreamTrackProcessor({ track: this.audioTrack });
      const audioEncoder = this.audioEncoder;
      const consumer = new WritableStream({
        write(audioData) {
          audioEncoder.encode(audioData);
          audioData.close();
        }
      });
      this.audioProcessor.readable.pipeTo(consumer);
    }


    this.startTime = (document.timeline.currentTime! as number);
    this.recording = true;
    this.lastKeyFrame = -Infinity;
    //  setInterval(() => this.encodeVideoFrame(), 1000 / 30);
  }

  public capture() {
    if (!this.recording || !this.startTime) return;

    if (this.lastFrame !== null) return;
    const elapsedTime = (document.timeline.currentTime as number) - this.startTime;

    const frame = new VideoFrame(this.canvas, { timestamp: elapsedTime * 1000 });
    const needsKeyFrame = elapsedTime - this.lastKeyFrame > 5;
    if (needsKeyFrame) this.lastKeyFrame = elapsedTime;

    this.lastFrame = { frame, needsKeyFrame };

    this.activeFrame++;
  }

  public encode() {
    if (!this.recording || !this.startTime) return;

    if (this.lastFrame !== null && this.videoEncoder) {

      if (this.videoEncoder.state === 'closed') {
        console.error("Video encoder is closed.");
        return;
      }

      this.videoEncoder.encode(this.lastFrame.frame, { keyFrame: this.lastFrame.needsKeyFrame });
      this.lastFrame.frame.close();
      this.lastFrame = null;
    }
  }

  public async stopRecording(): Promise<MediaRecorderResponse> {

    try {
      if (this.lastFrame !== null) {
        this.encode();
      }

      this.recording = false;

      await new Promise(resolve => setTimeout(resolve, 1000));
      // if (this.intervalId) clearInterval(this.intervalId);
      this.audioTrack?.stop();

      await this.videoEncoder?.flush();
      await this.audioEncoder?.flush();
      this.muxer!.finalize();

      const { buffer } = this.muxer!.target;

      this.videoEncoder = null;
      this.audioEncoder = null;
      this.lastFrame = null;
      this.audioTrack = null;
      this.muxer = null;
      this.startTime = null;
      this.lastKeyFrame = -Infinity;

      return {
        success: true,
        url: URL.createObjectURL(new Blob([buffer], { type: 'video/mp4' }))
      }
    }
    catch (e) {
      return {
        success: false,
        error: (e as Error).message,
        url: undefined
      }
    }
  }

  private downloadBlob(blob: Blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recording.mp4';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
